import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../../utils/fatch';
import styles from './Itinerary.module.css';

export const useItinerary = () => {
    const [itinerary, setItinerary] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch itinerary from backend on mount
    useEffect(() => {
        const fetchItinerary = async () => {
            try {
                const response = await fetch(`${API_URL}/itinerary`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
                
                if (response.ok) {
                    const data = await response.json();
                    // Map backend fields to frontend format
                    const mapped = data.itinerary.map(item => ({
                        place_id: item.place_id,
                        name: item.name,
                        rating: item.rating,
                        photo: item.photo_reference,
                        editorial_summary: item.editorial_summary,
                        position: item.position
                    }));
                    setItinerary(mapped);
                }
            } catch (error) {
                console.error('Error fetching itinerary:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchItinerary();
    }, []);

    const addToItinerary = useCallback(async (activity) => {
        // Optimistic update
        const newItem = {
            place_id: activity.place_id,
            name: activity.name || activity.title,
            rating: activity.rating,
            photo: activity.photos?.[0]?.photo_reference,
            editorial_summary: activity.editorial_summary?.overview,
        };

        // Check if already exists
        if (itinerary.some(item => item.place_id === activity.place_id)) {
            return;
        }

        setItinerary(prev => [...prev, newItem]);

        try {
            const response = await fetch(`${API_URL}/itinerary`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    place_id: activity.place_id,
                    name: activity.name || activity.title,
                    rating: activity.rating,
                    photo_reference: activity.photos?.[0]?.photo_reference,
                    editorial_summary: activity.editorial_summary?.overview,
                }),
            });

            if (!response.ok) {
                // Revert on failure
                setItinerary(prev => prev.filter(item => item.place_id !== activity.place_id));
                console.error('Failed to add to itinerary');
            }
        } catch (error) {
            // Revert on error
            setItinerary(prev => prev.filter(item => item.place_id !== activity.place_id));
            console.error('Error adding to itinerary:', error);
        }
    }, [itinerary]);

    const removeFromItinerary = useCallback(async (placeId) => {
        // Optimistic update
        const removedItem = itinerary.find(item => item.place_id === placeId);
        setItinerary(prev => prev.filter(item => item.place_id !== placeId));

        try {
            const response = await fetch(`${API_URL}/itinerary/${placeId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                // Revert on failure
                if (removedItem) {
                    setItinerary(prev => [...prev, removedItem]);
                }
                console.error('Failed to remove from itinerary');
            }
        } catch (error) {
            // Revert on error
            if (removedItem) {
                setItinerary(prev => [...prev, removedItem]);
            }
            console.error('Error removing from itinerary:', error);
        }
    }, [itinerary]);

    const clearItinerary = useCallback(async () => {
        // Optimistic update
        const previousItinerary = [...itinerary];
        setItinerary([]);

        try {
            const response = await fetch(`${API_URL}/itinerary`, {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                // Revert on failure
                setItinerary(previousItinerary);
                console.error('Failed to clear itinerary');
            }
        } catch (error) {
            // Revert on error
            setItinerary(previousItinerary);
            console.error('Error clearing itinerary:', error);
        }
    }, [itinerary]);

    const isInItinerary = useCallback((placeId) => {
        return itinerary.some(item => item.place_id === placeId);
    }, [itinerary]);

    return {
        itinerary,
        loading,
        addToItinerary,
        removeFromItinerary,
        clearItinerary,
        isInItinerary
    };
};

export const ItineraryButton = ({ activity, isInItinerary, addToItinerary, removeFromItinerary, onAdd }) => {
    const inItinerary = isInItinerary(activity.place_id);

    const handleClick = () => {
        if (inItinerary) {
            removeFromItinerary(activity.place_id);
        } else {
            addToItinerary(activity);
            onAdd?.();
        }
    };

    return (
        <button
            className={`${styles.addToItineraryBtn} ${inItinerary ? styles.addedToItinerary : ''}`}
            onClick={handleClick}
        >
            {inItinerary ? (
                <>
                    <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor">
                        <path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" />
                    </svg>
                    Added
                </>
            ) : 'Add to Itinerary'}
        </button>
    );
};

export const ItineraryFloatingButton = ({ count, onClick }) => {
    return (
        <button className={styles.floatingBtn} onClick={onClick}>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                <path d="M280-600v-80h560v80H280Zm0 160v-80h560v80H280Zm0 160v-80h560v80H280ZM160-600q-17 0-28.5-11.5T120-640q0-17 11.5-28.5T160-680q17 0 28.5 11.5T200-640q0 17-11.5 28.5T160-600Zm0 160q-17 0-28.5-11.5T120-480q0-17 11.5-28.5T160-520q17 0 28.5 11.5T200-480q0 17-11.5 28.5T160-440Zm0 160q-17 0-28.5-11.5T120-320q0-17 11.5-28.5T160-360q17 0 28.5 11.5T200-320q0 17-11.5 28.5T160-280Z" />
            </svg>
            {count > 0 && <span className={styles.badge}>{count}</span>}
        </button>
    );
};

export const ItineraryPanel = ({
    isOpen,
    onClose,
    itinerary,
    removeFromItinerary,
    clearItinerary
}) => {
    return (
        <>
            <div
                className={`${styles.overlay} ${isOpen ? styles.overlayVisible : ''}`}
                onClick={onClose}
            />
            <aside className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}>
                <div className={styles.header}>
                    <div className={styles.headerInfo}>
                        <svg xmlns="http://www.w3.org/2000/svg" height="28px" viewBox="0 -960 960 960" width="28px" fill="var(--color-primary, #4A90E2)">
                            <path d="M280-600v-80h560v80H280Zm0 160v-80h560v80H280Zm0 160v-80h560v80H280ZM160-600q-17 0-28.5-11.5T120-640q0-17 11.5-28.5T160-680q17 0 28.5 11.5T200-640q0 17-11.5 28.5T160-600Zm0 160q-17 0-28.5-11.5T120-480q0-17 11.5-28.5T160-520q17 0 28.5 11.5T200-480q0 17-11.5 28.5T160-440Zm0 160q-17 0-28.5-11.5T120-320q0-17 11.5-28.5T160-360q17 0 28.5 11.5T200-320q0 17-11.5 28.5T160-280Z" />
                        </svg>
                        <div>
                            <h2 className={styles.title}>My Itinerary</h2>
                            <p className={styles.subtitle}>
                                {itinerary.length} {itinerary.length === 1 ? 'place' : 'places'} added
                            </p>
                        </div>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                            <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                        </svg>
                    </button>

                </div>
                <div className={styles.header} style={{ fontSize: '12px' }}>

                    This Section is still in development, <br /> A full itinerary management experience is coming soon!
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                        <path d="M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" />
                    </svg>
                </div>

                <div className={styles.content}>
                    {itinerary.length === 0 ? (
                        <div className={styles.empty}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="64px" viewBox="0 -960 960 960" width="64px" fill="var(--text-muted)">
                                <path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm40-80h480L570-480 450-320l-90-120-120 160Zm-40 80v-560 560Z" />
                            </svg>
                            <h3>No places yet</h3>
                            <p>Start adding activities to build your perfect itinerary</p>
                        </div>
                    ) : (
                        <div className={styles.list}>
                            {itinerary.map((item, index) => (
                                <div key={item.place_id} className={styles.item}>
                                    <span className={styles.itemNumber}>{index + 1}</span>
                                    <div className={styles.itemImage}>
                                        {item.photo ? (
                                            <img
                                                src={`${API_URL}/place-photo?photoRef=${item.photo}`}
                                                alt={item.name}
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        ) : (
                                            <div className={styles.itemPlaceholder}>
                                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                                                    <path d="M480-480q33 0 56.5-23.5T560-560q0-33-23.5-56.5T480-640q-33 0-56.5 23.5T400-560q0 33 23.5 56.5T480-480Zm0 294q122-112 181-203.5T720-552q0-109-69.5-178.5T480-800q-101 0-170.5 69.5T240-552q0 71 59 162.5T480-186Zm0 106Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Z" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.itemInfo}>
                                        <Link to={`/activity_details/${item.place_id}`} className={styles.itemName}>
                                            {item.name}
                                        </Link>
                                        {item.rating && (
                                            <div className={styles.itemRating}>
                                                <svg xmlns="http://www.w3.org/2000/svg" height="14px" viewBox="0 -960 960 960" width="14px" fill="#FBBF24">
                                                    <path d="m354-287 126-76 126 77-33-144 111-96-146-13-58-136-58 135-146 13 111 97-33 143ZM233-120l65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Zm247-350Z" />
                                                </svg>
                                                <span>{item.rating}</span>
                                            </div>
                                        )}
                                        {item.editorial_summary && (
                                            <p className={styles.itemDesc}>{item.editorial_summary}</p>
                                        )}
                                    </div>
                                    <button
                                        className={styles.itemRemove}
                                        onClick={() => removeFromItinerary(item.place_id)}
                                        aria-label="Remove from itinerary"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor">
                                            <path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {itinerary.length > 0 && (
                    <div className={styles.footer}>
                        <button className={styles.clearBtn} onClick={clearItinerary}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="currentColor">
                                <path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Z" />
                            </svg>
                            Clear All
                        </button>
                        <button className={styles.saveBtn}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="currentColor">
                                <path d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z" />
                            </svg>
                            Save Itinerary
                        </button>
                    </div>
                )}
            </aside>
        </>
    );
};

// Main Itinerary Component (combines floating button + panel)
export const Itinerary = ({
    itinerary,
    removeFromItinerary,
    clearItinerary,
    isOpen,
    setIsOpen
}) => {
    return (
        <>
            <ItineraryFloatingButton
                count={itinerary.length}
                onClick={() => setIsOpen(true)}
            />
            <ItineraryPanel
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                itinerary={itinerary}
                removeFromItinerary={removeFromItinerary}
                clearItinerary={clearItinerary}
            />
        </>
    );
};

export default Itinerary;
